// ExpertiseRegistration — 5-step wizard modal for ecosystem profile registration
import { useState, useEffect, useRef } from 'react'
import {
  X, ArrowRight, ArrowLeft, Check, Plus,
  Lightbulb, Eye, Factory, TrendingUp, Users, BookOpen,
  Linkedin, CheckCircle, Clock, Globe,
} from 'lucide-react'
import {
  createEcosystemProfile,
  updateEcosystemProfile,
  getLinkedInAuthUrl,
  linkedInCallback,
} from '../../services/api'
import type {
  EcosystemProfile,
  EcosystemRole,
  AvailabilityType,
  LinkedInProfile,
  SkillCategory,
} from '../../types'

interface ExpertiseRegistrationProps {
  isOpen: boolean
  onClose: () => void
  onComplete: (profile: EcosystemProfile) => void
  existingProfile?: EcosystemProfile
}

const ROLES: { value: EcosystemRole; label: string; description: string; icon: typeof Lightbulb }[] = [
  { value: 'inventor', label: 'Inventor', description: 'Create and patent new innovations', icon: Lightbulb },
  { value: 'reviewer', label: 'Reviewer', description: 'Evaluate and provide expert feedback', icon: Eye },
  { value: 'manufacturer', label: 'Manufacturer', description: 'Produce and scale inventions', icon: Factory },
  { value: 'investor', label: 'Investor', description: 'Fund promising innovations', icon: TrendingUp },
  { value: 'advisor', label: 'Advisor', description: 'Guide strategy and connections', icon: Users },
  { value: 'researcher', label: 'Researcher', description: 'Conduct academic and applied research', icon: BookOpen },
]

const DOMAIN_OPTIONS = [
  'Healthcare', 'Automotive', 'Energy', 'Agriculture', 'Aerospace',
  'Finance', 'Education', 'Manufacturing', 'Telecommunications', 'Biotechnology',
  'Environmental', 'Robotics', 'Materials Science', 'Consumer Electronics', 'Defence',
]

const AVAILABILITY_OPTIONS: { value: AvailabilityType; label: string; description: string }[] = [
  { value: 'full_time', label: 'Full-time', description: 'Available for ongoing full-time engagements' },
  { value: 'part_time', label: 'Part-time', description: 'Available for regular part-time work' },
  { value: 'project_based', label: 'Project-based', description: 'Available for specific project scopes' },
  { value: 'advisory', label: 'Advisory', description: 'Available for consulting and advisory roles' },
]

const RATE_RANGES = ['$50-100', '$100-200', '$200-500', '$500+']

export default function ExpertiseRegistration({
  isOpen,
  onClose,
  onComplete,
  existingProfile,
}: ExpertiseRegistrationProps) {
  const [step, setStep] = useState(1)
  const [submitting, setSubmitting] = useState(false)

  // Step 1 — Role
  const [selectedRole, setSelectedRole] = useState<EcosystemRole | ''>('')

  // Step 2 — Profile
  const [name, setName] = useState('')
  const [headline, setHeadline] = useState('')
  const [bio, setBio] = useState('')
  const [avatarUrl, setAvatarUrl] = useState('')
  const [linkedInConnected, setLinkedInConnected] = useState(false)
  const [linkedInProfile, setLinkedInProfile] = useState<LinkedInProfile | null>(null)
  const [linkedInLoading, setLinkedInLoading] = useState(false)

  // Step 3 — Skills & Domains
  const [skills, setSkills] = useState<string[]>([])
  const [suggestedSkills, setSuggestedSkills] = useState<SkillCategory[]>([])
  const [newSkill, setNewSkill] = useState('')
  const [domains, setDomains] = useState<string[]>([])

  // Step 4 — Availability
  const [availability, setAvailability] = useState<AvailabilityType | ''>('')
  const [hourlyRate, setHourlyRate] = useState('')
  const [timezone, setTimezone] = useState('')

  // Pre-fill from existing profile
  useEffect(() => {
    if (existingProfile) {
      setSelectedRole(existingProfile.primary_role)
      setName(existingProfile.name)
      setHeadline(existingProfile.headline)
      setBio(existingProfile.bio)
      setAvatarUrl(existingProfile.avatar_url || '')
      setSkills(existingProfile.skills)
      setDomains(existingProfile.domains)
      setAvailability(existingProfile.availability)
      setHourlyRate(existingProfile.hourly_rate_range || '')
      if (existingProfile.linkedin_url) setLinkedInConnected(true)
    }
  }, [existingProfile])

  // Default timezone
  useEffect(() => {
    if (!timezone) {
      try {
        setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone)
      } catch {
        setTimezone('UTC')
      }
    }
  }, [timezone])

  // Listen for LinkedIn OAuth callback (produced by pages/auth/LinkedInCallback).
  // BUG-099: the listener is registered once ([]), so referencing
  // handleLinkedInCallback directly captured the FIRST render's state (name /
  // headline / avatarUrl always '') — a stale closure that clobbered anything
  // the user had typed. Route the message through a latest-ref instead.
  const linkedInCallbackRef = useRef<(code: string) => void>(() => {})
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only trust our own origin — the message carries an OAuth code.
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== 'linkedin-callback') return
      if (event.data.code) {
        linkedInCallbackRef.current(event.data.code)
      } else if (event.data.error) {
        setLinkedInLoading(false)
        alert('LinkedIn authorisation was declined or failed. Please try again.')
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  const handleLinkedInConnect = async () => {
    setLinkedInLoading(true)
    // BUG-099: open the popup synchronously BEFORE the await — window.open
    // after an async boundary loses the user-gesture context and gets blocked.
    const win = window.open('', 'linkedin-auth', 'width=600,height=700')
    try {
      const redirectUri = `${window.location.origin}/auth/linkedin/callback`
      const { url } = await getLinkedInAuthUrl(redirectUri)
      if (win && !win.closed) {
        win.location.href = url
      } else {
        alert('Popup was blocked. Please allow popups for this site and try again.')
        setLinkedInLoading(false)
        return
      }
    } catch {
      win?.close()
      alert('Failed to initiate LinkedIn authentication')
      setLinkedInLoading(false)
      return
    }
    setLinkedInLoading(false)
  }

  const handleLinkedInCallback = async (code: string) => {
    setLinkedInLoading(true)
    try {
      const redirectUri = `${window.location.origin}/auth/linkedin/callback`
      const { profile, suggested_skills } = await linkedInCallback(code, redirectUri)
      setLinkedInProfile(profile)
      setLinkedInConnected(true)
      if (profile.name && !name) setName(profile.name)
      if (profile.headline && !headline) setHeadline(profile.headline)
      if (profile.picture_url && !avatarUrl) setAvatarUrl(profile.picture_url)
      if (suggested_skills?.length) setSuggestedSkills(suggested_skills)
    } catch {
      alert('Failed to connect LinkedIn profile')
    }
    setLinkedInLoading(false)
  }
  // Keep the message listener pointed at the LATEST closure (BUG-099).
  linkedInCallbackRef.current = handleLinkedInCallback

  const toggleSkill = (skill: string) => {
    setSkills((prev) =>
      prev.includes(skill) ? prev.filter((s) => s !== skill) : [...prev, skill]
    )
  }

  const addManualSkill = () => {
    const trimmed = newSkill.trim()
    if (trimmed && !skills.includes(trimmed)) {
      setSkills((prev) => [...prev, trimmed])
      setNewSkill('')
    }
  }

  const toggleDomain = (domain: string) => {
    setDomains((prev) =>
      prev.includes(domain) ? prev.filter((d) => d !== domain) : [...prev, domain]
    )
  }

  const canProceed = (): boolean => {
    if (step === 1) return !!selectedRole
    if (step === 2) return !!name.trim()
    if (step === 3) return skills.length >= 1
    if (step === 4) return !!availability
    return true
  }

  const handleSubmit = async () => {
    if (!selectedRole || !availability) return
    setSubmitting(true)
    try {
      const data: Partial<EcosystemProfile> = {
        name: name.trim(),
        headline: headline.trim(),
        bio: bio.trim(),
        avatar_url: avatarUrl.trim() || undefined,
        // BUG-099: OpenID Connect exposes NO public profile URL — the old code
        // fabricated linkedin.com/in/{email}, which 404s (the /in/ segment
        // takes a vanity handle, never an email). Persist the stable member
        // URN from the `sub` claim instead; it marks the profile as
        // LinkedIn-verified without inventing a dead link.
        linkedin_url: linkedInProfile?.linkedin_sub ? `urn:li:person:${linkedInProfile.linkedin_sub}` : undefined,
        primary_role: selectedRole,
        skills,
        domains,
        availability,
        hourly_rate_range: hourlyRate || undefined,
      }
      const profile = existingProfile
        ? await updateEcosystemProfile(existingProfile.profile_id, data)
        : await createEcosystemProfile(data)
      onComplete(profile)
    } catch {
      alert('Failed to save ecosystem profile. Please try again.')
    }
    setSubmitting(false)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-sky-500" />
            <h2 className="text-base font-semibold text-gray-900">
              {existingProfile ? 'Edit Ecosystem Profile' : 'Ecosystem Profile Registration'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress indicators */}
        <div className="flex items-center justify-center gap-2 py-3 border-b bg-gray-50 shrink-0">
          {[1, 2, 3, 4, 5].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                s < step ? 'bg-green-500 text-white' :
                s === step ? 'bg-sky-500 text-white' :
                'bg-gray-200 text-gray-500'
              }`}>
                {s < step ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < 5 && <div className={`w-8 h-0.5 mx-1 ${s < step ? 'bg-green-300' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="p-5 overflow-y-auto flex-1">

          {/* Step 1 — Role Selection */}
          {step === 1 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-4">Select your primary ecosystem role</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {ROLES.map((role) => {
                  const Icon = role.icon
                  const selected = selectedRole === role.value
                  return (
                    <button
                      key={role.value}
                      onClick={() => setSelectedRole(role.value)}
                      className={`flex flex-col items-center gap-2 p-4 border-2 rounded-xl transition-colors text-center ${
                        selected
                          ? 'border-sky-500 bg-sky-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className={`w-7 h-7 ${selected ? 'text-sky-500' : 'text-gray-500'}`} />
                      <span className={`text-sm font-medium ${selected ? 'text-sky-700' : 'text-gray-900'}`}>
                        {role.label}
                      </span>
                      <span className="text-xs text-gray-500">{role.description}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Step 2 — Profile Information */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Tell us about yourself</p>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your full name"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Headline</label>
                <input
                  type="text"
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  placeholder="One-liner about your expertise"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Bio <span className="text-xs text-gray-500 font-normal">({bio.length}/500)</span>
                </label>
                <textarea
                  value={bio}
                  onChange={(e) => { if (e.target.value.length <= 500) setBio(e.target.value) }}
                  placeholder="Describe your background, experience, and what you bring to the ecosystem..."
                  rows={4}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Avatar URL</label>
                <input
                  type="url"
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  placeholder="https://example.com/avatar.jpg"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>

              <div className="pt-2">
                {linkedInConnected ? (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span>LinkedIn connected</span>
                    {linkedInProfile?.name && (
                      <span className="text-gray-500">({linkedInProfile.name})</span>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={handleLinkedInConnect}
                    disabled={linkedInLoading}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                    style={{ backgroundColor: '#0A66C2' }}
                  >
                    <Linkedin className="w-4 h-4" />
                    {linkedInLoading ? 'Connecting...' : 'Connect LinkedIn'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step 3 — Skills & Domains */}
          {step === 3 && (
            <div className="space-y-5">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Skills <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-500 font-normal ml-1">(at least 1 required)</span>
                </p>

                {suggestedSkills.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-1.5">AI-suggested from LinkedIn:</p>
                    {suggestedSkills.map((cat) => (
                      <div key={cat.category} className="mb-2">
                        <span className="text-xs font-medium text-gray-600">{cat.category}</span>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {cat.skills.map((skill) => (
                            <button
                              key={skill}
                              onClick={() => toggleSkill(skill)}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full border transition-colors ${
                                skills.includes(skill)
                                  ? 'bg-sky-100 border-sky-300 text-sky-700'
                                  : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                              }`}
                            >
                              {skills.includes(skill) && <Check className="w-3 h-3" />}
                              {skill}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {skills.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {skills.map((skill) => (
                      <span
                        key={skill}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full bg-sky-100 border border-sky-300 text-sky-700"
                      >
                        {skill}
                        <button onClick={() => toggleSkill(skill)} className="hover:text-sky-900">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addManualSkill() } }}
                    placeholder="Add a skill..."
                    className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                  <button
                    onClick={addManualSkill}
                    disabled={!newSkill.trim()}
                    className="flex items-center gap-1 px-3 py-2 text-xs bg-gray-100 border rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add
                  </button>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Domains</p>
                <div className="flex flex-wrap gap-1.5">
                  {DOMAIN_OPTIONS.map((domain) => (
                    <button
                      key={domain}
                      onClick={() => toggleDomain(domain)}
                      className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                        domains.includes(domain)
                          ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {domain}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 4 — Availability */}
          {step === 4 && (
            <div className="space-y-5">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">
                  Availability <span className="text-red-500">*</span>
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {AVAILABILITY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setAvailability(opt.value)}
                      className={`flex items-start gap-3 p-3 border-2 rounded-xl transition-colors text-left ${
                        availability === opt.value
                          ? 'border-sky-500 bg-sky-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <Clock className={`w-5 h-5 mt-0.5 shrink-0 ${
                        availability === opt.value ? 'text-sky-500' : 'text-gray-500'
                      }`} />
                      <div>
                        <span className={`text-sm font-medium ${
                          availability === opt.value ? 'text-sky-700' : 'text-gray-900'
                        }`}>
                          {opt.label}
                        </span>
                        <span className="block text-xs text-gray-500 mt-0.5">{opt.description}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Hourly Rate Range</label>
                <select
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                >
                  <option value="">Prefer not to say</option>
                  {RATE_RANGES.map((r) => (
                    <option key={r} value={r}>{r}/hr</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Timezone</label>
                <input
                  type="text"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  placeholder="e.g. Australia/Sydney"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
            </div>
          )}

          {/* Step 5 — Review & Submit */}
          {step === 5 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-4">Review your ecosystem profile</p>
              <div className="border rounded-xl p-5 bg-gray-50">
                {/* Profile preview card */}
                <div className="flex items-start gap-4">
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-sky-100 flex items-center justify-center border-2 border-white shadow-sm">
                      <span className="text-xl font-semibold text-sky-600">
                        {name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">{name}</h3>
                      {linkedInConnected && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                          <CheckCircle className="w-3 h-3" /> Verified
                        </span>
                      )}
                    </div>
                    {headline && (
                      <p className="text-sm text-gray-600 mt-0.5">{headline}</p>
                    )}
                    {selectedRole && (
                      <span className="inline-block mt-1.5 px-2.5 py-0.5 text-xs font-medium bg-sky-100 text-sky-700 rounded-full capitalize">
                        {selectedRole}
                      </span>
                    )}
                  </div>
                </div>

                {bio && (
                  <p className="text-sm text-gray-600 mt-4">{bio}</p>
                )}

                {skills.length > 0 && (
                  <div className="mt-4">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Skills</span>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {skills.map((skill) => (
                        <span key={skill} className="px-2 py-0.5 text-xs bg-sky-100 text-sky-700 rounded-full">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {domains.length > 0 && (
                  <div className="mt-3">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Domains</span>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {domains.map((domain) => (
                        <span key={domain} className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded-full">
                          {domain}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                  {availability && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {AVAILABILITY_OPTIONS.find((a) => a.value === availability)?.label}
                    </span>
                  )}
                  {hourlyRate && <span>{hourlyRate}/hr</span>}
                  {timezone && (
                    <span className="flex items-center gap-1">
                      <Globe className="w-3.5 h-3.5" />
                      {timezone}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer navigation */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50 shrink-0">
          <button
            onClick={onClose}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Cancel
          </button>
          <div className="flex items-center gap-2">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 border rounded-lg hover:bg-gray-100"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
            )}
            {step < 5 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-sky-500 text-white rounded-lg hover:bg-sky-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next <ArrowRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex items-center gap-1 px-4 py-1.5 text-xs bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="w-3.5 h-3.5" /> {submitting ? 'Registering...' : 'Register'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
